import { useEffect, useRef, useState } from 'react';
import styles from './styles.module.scss';
import TabContentWrapper from 'pages/Profile/components/TabContentWrapper';
import { Spinner, SearchFieldWithDebounce, Button } from 'components';
import classNames from 'classnames';
import { Popover } from 'components-antd';
import { Icons } from '../../components/icons';
import TaskFilters from './TasksFilters';
import TasksTable from './TasksTable';
import { showErrorMessage } from 'helpers';
import { getTeamMemberTasks } from 'api/teamList';
import { tasksStatusesIds } from 'settings/constants/transactionTasks';
import { displayListedStatus, displayTransactionStatuses } from 'settings/constants/transaction';
import { normalizeAddress } from 'components/ClientsDrawer/ProfileClient/Contact/components/Address/helpers';
import { TEAM_BASIC, transactionRoles } from 'settings/constants/roles';
import { getDateOnly } from 'helpers';
import moment from 'moment';
import ReassignTaskModal from './ReassignTaskModal';
import { useDispatch, useSelector } from 'react-redux';
import { requestGetTeamListEffect } from 'store/effects';
import { Icons as WorkshopIcons } from 'pages/Workshop/Icons';
import {
  TaskFiltersType,
  initialTasksFilters,
  PreparedTaskType,
  TasksStatus,
  TaskType,
} from './types';
import { getUserId } from 'store/selectors/user';
import { getAgentTeamRoleSelector } from 'store/selectors/agentTeamDetail';

const Tasks = ({ contactId }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState<boolean>(false);
  const [multiSelect, setMultiSelect] = useState<boolean>(false);
  const [popoverOpen, setPopoverOpen] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [tasksList, setTasksList] = useState<PreparedTaskType[]>([]);
  const [selectedTasksIds, setSelectedTasksIds] = useState<number[]>([]);
  const [filters, setFilters] = useState<TaskFiltersType>(initialTasksFilters);
  const [filtersActive, setFiltersActive] = useState<boolean>(false);
  const [showSearchField, setShowSearchField] = useState<boolean>(false);
  const [allSelected, setAllSelected] = useState<boolean>(false);
  const dateFormat = 'M/D/YYYY';
  const agentRole = useSelector(getAgentTeamRoleSelector);
  const userId = useSelector(getUserId);
  const countRef = useRef<number>(0);
  const limitedAccess = userId === contactId && agentRole === TEAM_BASIC;

  if (!contactId) return null;

  useEffect(() => {
    dispatch(requestGetTeamListEffect());
    fetchTeamMemberTasks();
  }, []);

  const fetchTeamMemberTasks = async () => {
    setLoading(true);
    try {
      const response = await getTeamMemberTasks({}, { agentId: contactId });
      if (response.status === 200 && response.data?.length) {
        const preparedTasks: PreparedTaskType[] = response.data.map((item) => {
          const taskType = getTasksTypeAndSubType(item);
          return {
            Id: item.Id,
            Description: item.Description,
            DueDate: item.DueDate,
            Status: item.Status,
            Title: item.Title || '-',
            Type: taskType.type || '-',
            SubType: taskType.subType,
            Campaign: item.Campaign,
            CampaignId: item.CampaignId,
            Address: item.Address,
            TransactionName: item.Transaction?.Property?.Address?.Line1 ?? '-',
            FormattedDate: item.DueDate ? getDateOnly(item.DueDate, dateFormat) : null,
            Transaction: item.Transaction
              ? {
                  ...item.Transaction,
                  Status: item.Transaction.Status?.Name || null,
                }
              : null,
          };
        });
        setTasksList(preparedTasks);
      }
    } catch (e) {
      showErrorMessage('Unable to fetch team member tasks.');
    }
    setLoading(false);
  };

  const getTasksTypeAndSubType = ({
    CampaignId,
    Campaign,
    Address,
    Transaction,
  }): {
    type: string;
    subType: string;
  } => {
    const transactionType = (roles: any[] = []) => {
      if (!roles.includes(transactionRoles.BUYER) && roles.includes(transactionRoles.SELLER))
        return 'Listing';
      else if (roles.includes(transactionRoles.BUYER) && !roles.includes(transactionRoles.SELLER))
        return 'Purchase';
      else return 'Dual';
    };

    const getCategory = () => {
      const { IsProject, Status, RepresentingRoles } = Transaction ?? {};
      return {
        type: IsProject ? 'Project' : transactionType(RepresentingRoles),
        subType: displayListedStatus[Status] ?? displayTransactionStatuses[Status] ?? Status.Name,
      };
    };

    const getCampaignCategory = () => {
      return {
        type: Campaign?.Name,
        subType: normalizeAddress(Address)?.line1,
      };
    };

    if (CampaignId) return getCampaignCategory();

    return getCategory();
  };

  const tasksFilterHandler = (taskFilters: TaskFiltersType | null) => {
    const haveValidFilters =
      taskFilters?.endDate ||
      taskFilters?.startDate ||
      taskFilters?.taskTypes?.length ||
      taskFilters?.taskStatuses?.length;
    setFiltersActive(haveValidFilters);
    setFilters(taskFilters ?? initialTasksFilters);
    setPopoverOpen(false);
  };

  const getFilterButtonAndPopover = () => (
    <Popover
      placement="leftTop"
      title={null}
      content={
        <TaskFilters
          setOpen={setPopoverOpen}
          open={popoverOpen}
          filters={filters}
          tasksFilterHandler={tasksFilterHandler}
        />
      }
      trigger="click"
      overlayClassName={styles.filtersPopover}
      open={popoverOpen}
      onOpenChange={(newOpen: boolean) => {
        setPopoverOpen(newOpen);
      }}
    >
      <div
        className={classNames(styles.filterButton, { [styles.filterButtonActive]: popoverOpen })}
      >
        <Icons variant={Icons.FILTER} />
      </div>
    </Popover>
  );

  const getFilteredTasks = () => {
    if (!tasksList?.length) return [];

    let filteredTasks: PreparedTaskType[] = [];
    if (filters?.taskStatuses?.includes(TasksStatus.all)) {
      filteredTasks = [...tasksList];
    } else if (filters?.taskStatuses?.length) {
      const statusFilterMap = {
        [TasksStatus.inProgress]: tasksStatusesIds.inProgress,
        [TasksStatus.done]: tasksStatusesIds.done,
        [TasksStatus.stuck]: tasksStatusesIds.stuck,
        [TasksStatus.overdue]: tasksStatusesIds.overdue,
        [TasksStatus.new]: tasksStatusesIds.new,
        [TasksStatus.na]: tasksStatusesIds.na,
      };

      filteredTasks = tasksList.filter((task) =>
        filters.taskStatuses.some((status) => task.Status === statusFilterMap[status]),
      );
    }

    filteredTasks = Array.from(new Map(filteredTasks.map((task) => [task.Id, task])).values());

    if (filters?.taskTypes?.length) {
      const typeFilterMap = {
        [TaskType.Dual]: TaskType.Dual,
        [TaskType.Listing]: TaskType.Listing,
        [TaskType.Project]: TaskType.Project,
        [TaskType.Purchase]: TaskType.Purchase,
      };

      filteredTasks = filteredTasks.filter((task) =>
        filters.taskTypes.some((type) => task.Type === typeFilterMap[type]),
      );
    }

    const start = filters.startDate ? moment(filters.startDate, dateFormat) : null;
    const end = filters.endDate ? moment(filters.endDate, dateFormat) : null;

    if (start || end) {
      filteredTasks = filteredTasks.filter((item) => {
        const itemDate = moment(item.FormattedDate, dateFormat);
        const isAfterStart = !start || itemDate.isSameOrAfter(start, 'day');
        const isBeforeEnd = !end || itemDate.isSameOrBefore(end, 'day');
        return isAfterStart && isBeforeEnd;
      });
    }

    filteredTasks = filteredTasks.sort((a, b) => {
      const dateA = moment(a.DueDate).valueOf();
      const dateB = moment(b.DueDate).valueOf();
      return dateA - dateB;
    });

    setTasksCount(filteredTasks.length);
    return filteredTasks;
  };

  const handleModalClose = (done: boolean) => {
    if (done) {
      setMultiSelect(false);
      const remainingTasks = tasksList.filter((task) => !selectedTasksIds.includes(task.Id));
      setTasksList(remainingTasks);
      setSelectedTasksIds([]);
    }
    setShowModal(false);
  };

  const onSearchBlurHandler = () => {
    if (!searchQuery) setShowSearchField(false);
  };

  const getFooter = () => {
    if (!multiSelect) return;

    return (
      <div className={styles.footer}>
        <div className={styles.left}>{`${selectedTasksIds.length} tasks selected`}</div>
        <div className={styles.right}>
          <Button
            type="submit"
            className={classNames(styles.resetButton, styles.sharedButton)}
            title={'Reset'}
            onClick={() => {
              setSelectedTasksIds([]);
              setAllSelected(false);
            }}
            disabled={selectedTasksIds.length === 0}
            iconLeft={<Icons variant={Icons.REASSIGN} color={'#676767'} />}
          />
          <Button
            type="submit"
            className={classNames(styles.reassignButton, styles.sharedButton)}
            title={'Reassign'}
            onClick={() => setShowModal(true)}
            disabled={selectedTasksIds.length === 0}
          />
        </div>
      </div>
    );
  };

  const getTasks = () => {
    if (!tasksList?.length) {
      setTasksCount(0);
      return [];
    }

    const filteredTasks = tasksList
      .filter((task) => ![tasksStatusesIds.done, tasksStatusesIds.na].includes(task.Status))
      .sort((a, b) => {
        const dateA = moment(a.DueDate).valueOf();
        const dateB = moment(b.DueDate).valueOf();
        return dateA - dateB;
      });

    setTasksCount(filteredTasks.length);
    return filteredTasks;
  };

  const setTasksCount = (count) => {
    countRef.current = count;
  };

  if (loading)
    return (
      <TabContentWrapper setMargin contentHeightFull>
        <div className={styles.tasksTab}>
          <div className={classNames(styles.content, styles.contentLoading)}>
            <Spinner />
          </div>
        </div>
      </TabContentWrapper>
    );
  else if (!loading && !tasksList.length)
    return (
      <TabContentWrapper setMargin contentHeightFull>
        <div className={styles.tasksTab}>
          <div className={styles.content}>
            <div className={styles.empty}>No tasks assigned.</div>
          </div>
        </div>
      </TabContentWrapper>
    );

  return (
    <TabContentWrapper
      setMargin
      contentHeight={multiSelect ? '80%' : '90%'}
      absoluteChild={getFooter()}
    >
      <div className={styles.tasksTab}>
        <div className={styles.header}>
          <div className={styles.left}>
            Tasks
            <span>{` (${countRef.current})`}</span>
          </div>
          <div className={styles.right}>
            {showSearchField ? (
              <SearchFieldWithDebounce
                className={styles.searchField}
                doNotDispatch
                sendAction={(value: string) => setSearchQuery(value)}
                placeholder="Search"
                isExpandedVersion
                onBlurHandler={onSearchBlurHandler}
                resetAction={() => setSearchQuery('')}
              />
            ) : (
              <div className={styles.icon} onClick={() => setShowSearchField(true)}>
                <WorkshopIcons variant={WorkshopIcons.SEARCH} color="#262626" />
              </div>
            )}
            {getFilterButtonAndPopover()}
            {limitedAccess ? null : (
              <div
                className={classNames(styles.icon, multiSelect ? styles.iconSelected : undefined)}
                onClick={() => {
                  if (!tasksList.length) return;
                  if (multiSelect) {
                    setSelectedTasksIds([]);
                    setAllSelected(false);
                  }
                  setMultiSelect((prev) => !prev);
                }}
              >
                <Icons variant={Icons.MULTISELECT} color="#262626" />
              </div>
            )}
          </div>
        </div>
        <div className={styles.content}>
          <TasksTable
            tasks={filtersActive ? getFilteredTasks() : getTasks()}
            multiSelect={multiSelect}
            searchQuery={searchQuery}
            selectedTasksIds={selectedTasksIds}
            setSelectedTasksIds={setSelectedTasksIds}
            allSelected={allSelected}
            setAllSelected={setAllSelected}
          />
        </div>
      </div>
      {showModal && (
        <ReassignTaskModal
          handleClose={handleModalClose}
          open={showModal}
          tasks={tasksList.filter((task) => selectedTasksIds.includes(task.Id))}
          contactId={contactId}
        />
      )}
    </TabContentWrapper>
  );
};

export default Tasks;
